import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Fab, IconButton, Snackbar, Tooltip } from '@mui/material';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare';
import Peer from 'simple-peer';
import { recordingSocket, socket } from '../../../config/SocketApi';
import { getIceConfig } from '../../../config/iceConfig';
import { destroyPeer, safeSignalPeer, stopMediaStream } from '../helpers/realtimeUtils';

const blobToBase64 = async (blob) => {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    const chunkSize = 0x8000;
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += chunkSize) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
    }
    return window.btoa(binary);
};

const getShareDimensions = (stream, direction) => {
    const settings = stream?.getVideoTracks?.()[0]?.getSettings?.() || {};
    const width = Math.max(2, Math.floor(settings.width || window.screen.width || window.innerWidth || 1280));
    const height = Math.max(2, Math.floor(settings.height || window.screen.height || window.innerHeight || 720));
    return {
        x: 0,
        y: 0,
        width,
        height,
        fullWidth: width,
        fullHeight: height,
        PR: 1,
        screenSharing: true,
        dir: direction,
    };
};

const StudentScreenShareControl = ({ roomId, direction = 'ltr', mobile = false }) => {
    const [isSharing, setIsSharing] = useState(false);
    const [error, setError] = useState('');
    const streamRef = useRef(null);
    const teacherPeerRef = useRef(null);
    const recorderRef = useRef(null);
    const startingRef = useRef(false);
    const teacherLookupTimerRef = useRef();

    const stopRecording = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try {
                recorder.stop();
            } catch (recordingError) {
                console.log('[StudentScreenShare] Recorder stop ignored:', recordingError.message);
            }
        }
    }, []);

    const stopSharing = useCallback(({ notifyTeacher = true } = {}) => {
        clearTimeout(teacherLookupTimerRef.current);
        stopRecording();
        destroyPeer(teacherPeerRef.current, 'student screen-share peer');
        teacherPeerRef.current = null;
        stopMediaStream(streamRef.current);
        streamRef.current = null;
        startingRef.current = false;
        setIsSharing(false);
        if (notifyTeacher && socket.connected) {
            socket.emit('share-screen-close-stream-to-teacher', { room_id: roomId });
        }
    }, [roomId, stopRecording]);

    const startRecording = useCallback((stream) => {
        if (!recordingSocket.connected || !stream?.getVideoTracks?.().length || typeof MediaRecorder === 'undefined') {
            return;
        }

        const mimeType = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
            .find((type) => MediaRecorder.isTypeSupported(type));
        const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        const dimensions = getShareDimensions(stream, direction);
        recorderRef.current = recorder;

        recordingSocket.emit('server-share-screen', { source: 'student-local-screen' }, (filePath) => {
            if (!filePath || recorder.state === 'inactive') return;
            let sendTail = Promise.resolve();
            recorder.ondataavailable = (event) => {
                if (!event.data || event.data.size === 0) return;
                sendTail = sendTail.then(async () => {
                    const data = await blobToBase64(event.data);
                    recordingSocket.emit('share_screen_recording_data', JSON.stringify({ data, file_path: filePath }));
                });
            };
            recorder.onstop = async () => {
                await sendTail.catch((flushError) => {
                    console.log('[StudentScreenShare] Chunk flush failed:', flushError.message);
                });
                recordingSocket.emit('share_screen_recording_data', JSON.stringify({
                    ...dimensions,
                    captureVersion: 2,
                    file_path: filePath,
                }));
                if (recorderRef.current === recorder) recorderRef.current = null;
            };
            recorder.onerror = (event) => {
                console.log('[StudentScreenShare] Recorder failed:', event.error?.message || event.type);
            };
            recorder.start(500);
        });
    }, [direction]);

    const createTeacherPeer = useCallback((teacherSocket, stream) => {
        if (!teacherSocket?.socket_id || !stream?.active) {
            setError('The tutor is not connected yet. Please try again.');
            stopSharing({ notifyTeacher: false });
            return;
        }

        destroyPeer(teacherPeerRef.current, 'replace student screen-share peer');
        const dimensions = getShareDimensions(stream, direction);
        const peer = new Peer({
            initiator: true,
            trickle: true,
            stream,
            config: getIceConfig(),
        });
        peer.on('signal', (signal) => {
            socket.emit('student-share-screen-signal-to-teacher', {
                signal,
                studentSocketId: socket.id,
                teacherSocketId: teacherSocket.socket_id,
                shareScreenDimension: dimensions,
                room_id: roomId,
            });
        });
        peer.on('connect', () => setIsSharing(true));
        peer.on('error', (peerError) => {
            console.log('[StudentScreenShare] Peer failed:', peerError.message);
            setError('Screen sharing was interrupted. Please try again.');
            stopSharing();
        });
        teacherPeerRef.current = peer;
    }, [direction, roomId, stopSharing]);

    const startSharing = useCallback(async () => {
        if (startingRef.current || isSharing) return;
        if (!navigator.mediaDevices?.getDisplayMedia) {
            setError('Screen sharing is not supported by this browser. Use the latest Chrome or Edge browser.');
            return;
        }

        startingRef.current = true;
        setError('');
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
            if (!stream?.getVideoTracks?.().length) throw new Error('No screen video track was selected.');
            streamRef.current = stream;
            setIsSharing(true);
            stream.getTracks().forEach((track) => {
                track.onended = () => stopSharing();
            });
            startRecording(stream);

            let teacherFound = false;
            socket.emit('get-teacher-socket', (teacherSocket) => {
                teacherFound = true;
                clearTimeout(teacherLookupTimerRef.current);
                createTeacherPeer(teacherSocket, stream);
            });
            teacherLookupTimerRef.current = setTimeout(() => {
                if (!teacherFound) {
                    setError('The tutor is not connected yet. Please try again.');
                    stopSharing({ notifyTeacher: false });
                }
            }, 5000);
        } catch (shareError) {
            startingRef.current = false;
            if (shareError?.name !== 'NotAllowedError') {
                setError(shareError?.message || 'Unable to start screen sharing.');
            }
        }
    }, [createTeacherPeer, isSharing, startRecording, stopSharing]);

    useEffect(() => {
        const handleTeacherSignal = (data = {}) => {
            safeSignalPeer(teacherPeerRef.current, data.signal, 'student screen-share answer');
        };
        socket.on('teacher-share-screen-signal', handleTeacherSignal);
        return () => socket.off('teacher-share-screen-signal', handleTeacherSignal);
    }, []);

    useEffect(() => () => stopSharing(), [stopSharing]);

    const label = isSharing ? 'Stop sharing screen' : 'Share screen';
    const icon = isSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />;

    return (
        <>
            <Tooltip title={label}>
                {mobile ? (
                    <Fab
                        color={isSharing ? 'error' : 'secondary'}
                        size="medium"
                        aria-label={label}
                        onClick={isSharing ? () => stopSharing() : startSharing}
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            right: 80,
                            zIndex: 10,
                        }}
                    >
                        {icon}
                    </Fab>
                ) : (
                    <IconButton aria-label={label} onClick={isSharing ? () => stopSharing() : startSharing}>
                        {React.cloneElement(icon, { color: isSharing ? 'error' : 'secondary' })}
                    </IconButton>
                )}
            </Tooltip>
            <Snackbar open={Boolean(error)} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert severity="warning" onClose={() => setError('')} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default StudentScreenShareControl;
